import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import Page from "../components/ui/Page";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Spinner from "../components/ui/Spinner";
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from "../components/ui/Card";
import ThoughtTrace from "../components/agent/ThoughtTrace";
import { api } from "../services/api";
import { getApiBase } from "../services/apiBase";
import { getWorkspaceId, isAuthenticated } from "../services/authStorage";
import { useLiveIntegrity } from "../hooks/useLiveIntegrity";
import { getCsrfHeader } from "../services/csrf";

type Exhibit = {
  id: string;
  filename: string;
  integrityHash?: string | null;
};

const buildPseudoHash = (input: string) => {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  const hex = (hash >>> 0).toString(16).padStart(8, "0");
  return `0x${hex}${hex}`.slice(0, 20);
};

export default function CommandCenter() {
  const navigate = useNavigate();
  const workspaceId = useMemo(() => getWorkspaceId(), []);
  const [exhibits, setExhibits] = useState<Exhibit[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState<string | null>(null);
  const [scrutinyLog, setScrutinyLog] = useState<string[]>([]);
  const [batesAssigned, setBatesAssigned] = useState<string[]>([]);
  const [analysisTarget, setAnalysisTarget] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ingestNotice, setIngestNotice] = useState<string | null>(null);
  const [ingestActive, setIngestActive] = useState(false);
  const [diagnosticMode, setDiagnosticMode] = useState(false);
  const [auditQuery, setAuditQuery] = useState("");
  const [auditAnswer, setAuditAnswer] = useState<string | null>(null);
  const [auditFacts, setAuditFacts] = useState<string[]>([]);
  const [auditBusy, setAuditBusy] = useState(false);
  const liveIntegrity = useLiveIntegrity(workspaceId);
  const lastEventRef = useRef<string>("");
  const authed = isAuthenticated();

  useEffect(() => {
    if (!workspaceId) return;
    api.get(`/workspaces/${workspaceId}/exhibits`)
      .then((rows: any) => setExhibits(Array.isArray(rows) ? rows : []))
      .catch(() => setExhibits([]));
  }, [workspaceId]);

  useEffect(() => {
    const next = sessionStorage.getItem("lexipro_diag_mode") === "1";
    setDiagnosticMode(next);
  }, []);

  const runTacticalIngest = useCallback(async (files: File[]) => {
    if (!files.length) return;
    if (!workspaceId) {
      setError("Workspace missing. Sign in to create or select a workspace.");
      return;
    }
    setUploading(true);
    setIngestActive(true);
    setError(null);
    setIngestNotice(null);
    setProgress(0);
    setStage("INGESTING");
    setScrutinyLog([]);
    setBatesAssigned([]);
    setAnalysisTarget(files.map((file) => file.name).join(", "));

    const headers: Record<string, string> = {
      "x-workspace-id": workspaceId,
      ...getCsrfHeader()
    };

    try {
      const uploaded: Exhibit[] = [];
      for (const [idx, file] of files.entries()) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${getApiBase()}/exhibits`, {
          method: "POST",
          headers,
          body: formData,
          credentials: "include"
        });
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(text || `Ingest failed (${res.status})`);
        }
        const data = await res.json().catch(() => ({}));
        uploaded.push({
          id: String(data?.id || `${Date.now()}-${idx}`),
          filename: String(data?.filename || file.name),
          integrityHash: data?.integrityHash || null
        });
        setProgress(Math.round(((idx + 1) / files.length) * 100));
      }
      if (uploaded.length) {
        setExhibits((prev) => [...uploaded, ...prev]);
      }
      const assigned = uploaded.length
        ? uploaded.map((entry, idx) => {
          const label = entry.integrityHash ? entry.integrityHash.slice(0, 12) : `LEX-${String(idx + 1).padStart(6, "0")}`;
          return `${label} - ${entry.filename}`;
        })
        : files.map((file, idx) => `LEX-${String(idx + 1).padStart(6, "0")} - ${file.name}`);
      setBatesAssigned(assigned);
      setIngestNotice("Evidence recorded. Pending verification.");
    } catch (e: any) {
      setError(e?.message || "Ingest failed.");
    }
    setUploading(false);
    setIngestActive(false);
  }, [workspaceId]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    void runTacticalIngest(acceptedFiles);
  }, [runTacticalIngest]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true
  });

  useEffect(() => {
    const events = liveIntegrity.events;
    if (!events.length) {
      if (!ingestActive) {
        setStage(null);
        setProgress(0);
      }
      return;
    }

    const hasEvent = (label: string) => events.some((event) => event.action === label);
    const ingestStarted = hasEvent("INGEST_STARTED") || hasEvent("EXHIBIT_UPLOAD");
    const hashSealed = hasEvent("HASH_SEALED") || hasEvent("ROOT_HASH_SEALED");
    const ruleScan = hasEvent("RULE_SCAN_COMPLETE");
    const batesStamped = hasEvent("BATES_STAMPED");

    let nextStage: string | null = null;
    let nextProgress = 0;
    if (batesStamped) {
      nextStage = "COMPLETE";
      nextProgress = 100;
    } else if (ruleScan) {
      nextStage = "BATES ANCHOR";
      nextProgress = 80;
    } else if (hashSealed) {
      nextStage = "RULE SCAN";
      nextProgress = 55;
    } else if (ingestStarted) {
      nextStage = "BIT-SEALING";
      nextProgress = 25;
    }

    if (nextStage) setStage(nextStage);
    setProgress(nextProgress);

    const newest = events[0];
    const newestKey = newest?.id || `${newest?.action}-${newest?.timestamp}`;
    if (newestKey && newestKey !== lastEventRef.current) {
      const line = `> [${newest.timestamp}] ${newest.action}`;
      setScrutinyLog((prev) => [line, ...prev].slice(0, 8));
      lastEventRef.current = newestKey;
    }

    if (batesStamped) {
      setUploading(false);
      setIngestActive(false);
      setIngestNotice("Evidence recorded.");
    }
  }, [ingestActive, liveIntegrity.events]);

  const exportPacket = async () => {
    if (!workspaceId) return;
    let target = exhibits[0];
    if (!target) {
      try {
        const rows = await api.get(`/workspaces/${workspaceId}/exhibits`);
        if (Array.isArray(rows) && rows.length) {
          setExhibits(rows);
          target = rows[0];
        }
      } catch {
        // ignore and fall through
      }
    }
    if (!target) {
      setError("No verified exhibits available to export.");
      return;
    }
    setExporting(true);
    setError(null);
    try {
      const res = await fetch(`${getApiBase()}/exhibits/${target.id}/package`, {
        credentials: "include",
        headers: { "x-workspace-id": workspaceId }
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `case_${target.id}_admissibility.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const scatterDots = useMemo(() => {
    return exhibits.slice(0, 14).map((exhibit, idx) => {
      const weight = (exhibit.filename.length % 10) / 10;
      const certainty = exhibit.integrityHash ? 0.8 : 0.45;
      return {
        id: exhibit.id || `${idx}`,
        x: 10 + weight * 80,
        y: 10 + (1 - certainty) * 80,
        tone: exhibit.integrityHash ? "bg-emerald-400" : "bg-amber-400"
      };
    });
  }, [exhibits]);

  const toggleDiagnostic = () => {
    const next = !diagnosticMode;
    setDiagnosticMode(next);
    sessionStorage.setItem("lexipro_diag_mode", next ? "1" : "0");
    window.dispatchEvent(new CustomEvent("lexipro:diag-mode", { detail: { enabled: next } }));
  };

  const runSelfAudit = async () => {
    if (!workspaceId) return;
    setAuditBusy(true);
    setAuditAnswer(null);
    setAuditFacts([]);
    try {
      const res = await fetch(`${getApiBase()}/system/self-audit`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-workspace-id": workspaceId
        },
        body: JSON.stringify({ query: auditQuery })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Self-audit failed (${res.status})`);
      }
      const data = await res.json();
      setAuditAnswer(String(data?.answer || "No response."));
      setAuditFacts(Array.isArray(data?.facts) ? data.facts : []);
    } catch (e: any) {
      setAuditAnswer(e?.message || "Self-audit failed.");
    } finally {
      setAuditBusy(false);
    }
  };

  if (!authed || !workspaceId) {
    return (
      <Page
        title="Tactical Ingest Gate"
        subtitle="Bit-Sealing + Compliance + Bates in one forensic workflow"
      >
        <div className="rounded-3xl border border-white/10 bg-slate-950/70 p-8 text-center">
          <div className="text-xs uppercase tracking-[0.4em] text-slate-500">Ingest Evidence</div>
          <h2 className="mt-4 text-2xl font-semibold text-white">Seal evidence into the immutable ledger.</h2>
          <p className="mt-3 text-sm text-slate-400">
            Every upload is hashed, governed, and ready for court-grade verification.
          </p>
          <div className="mt-6 flex items-center justify-center">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (!authed) {
                  navigate("/login");
                  return;
                }
                navigate("/");
              }}
            >
              Ingest evidence
            </Button>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page
      title="Tactical Ingest Gate"
      subtitle="Bit-Sealing + Compliance + Bates in one forensic workflow"
      right={
        <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.3em]">
          <Badge tone="blue">SYSTEM_MODE: SOVEREIGN</Badge>
          <Badge tone="green">LEDGER: SYNCHRONIZED</Badge>
          <Badge tone="amber">AI_SENTINEL: ANALYZING</Badge>
          <Badge tone="green">STATUS: FIPS 140-2 READY</Badge>
          <Badge tone="blue">INTEGRATION_TIME: 48 MINS</Badge>
        </div>
      }
    >
      {error ? (
        <div className="mb-4 rounded-2xl border border-red-500/40 bg-red-500/15 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      ) : null}
      {ingestNotice ? (
        <div className="mb-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
          {ingestNotice}
        </div>
      ) : null}
      {!exhibits.length ? (
        <div className="mb-4 rounded-3xl border border-white/10 bg-slate-950/70 p-6 text-center">
          <div className="text-xs uppercase tracking-[0.4em] text-slate-500">No Evidence Yet</div>
          <h2 className="mt-3 text-xl font-semibold text-white">Ingest evidence to begin chain-of-custody.</h2>
          <p className="mt-2 text-sm text-slate-400">
            The ledger stays empty until your first verified upload is sealed.
          </p>
          <div className="mt-4 flex items-center justify-center">
            <Button variant="primary" size="sm">
              Ingest evidence
            </Button>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-8 space-y-4">
          <div
            {...getRootProps()}
            id="upload-dropzone"
            className={[
              "rounded-3xl border border-dashed p-8 text-center transition-all bg-slate-950/60",
              isDragActive ? "border-emerald-400/60 bg-emerald-500/10" : "border-slate-700",
              uploading ? "opacity-80" : ""
            ].join(" ")}
          >
            <input {...getInputProps()} />
            <div className="text-[10px] uppercase tracking-[0.4em] text-slate-500">Tactical Ingest Drop-Zone</div>
            <div className="mt-3 text-sm text-slate-200">
              Drop discovery files to seal, scan, and Bates-anchor in parallel.
            </div>
          <div className="mt-3 text-xs font-mono text-emerald-300">
            {analysisTarget ? `Target: ${analysisTarget}` : "Awaiting discovery payload"}
          </div>
          {error ? (
            <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {error}
            </div>
            ) : null}
            {uploading ? (
              <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-black/50 p-4">
                <div className="flex items-center justify-between text-xs text-slate-300">
                  <span>{stage || "ANALYZING"}</span>
                  <span className="font-mono">{progress}%</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
            ) : (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs">
                <Button variant="secondary" size="sm">
                  <i className="fa-solid fa-folder-open" />
                  Select Discovery Folder
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={exportPacket}
                  disabled={exporting}
                  id="btn-export-packet"
                >
                  {exporting ? <Spinner size={14} /> : <i className="fa-solid fa-file-zipper" />}
                  Seal & Export Packet
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-slate-950/60 border border-white/10 lexi-sector">
              <CardHeader>
                <CardTitle>Rule Scan Log</CardTitle>
                <CardSubtitle>FRE 902 / FRE 1002 compliance track</CardSubtitle>
              </CardHeader>
              <CardBody>
                <div className="rounded-2xl border border-white/10 bg-black/60 p-4 font-mono text-[11px] text-emerald-200 space-y-2">
                  {scrutinyLog.length ? (
                    scrutinyLog.map((line, idx) => <div key={`${line}-${idx}`}>{line}</div>)
                  ) : (
                    <div className="text-slate-500">Awaiting stress-test execution.</div>
                  )}
                </div>
              </CardBody>
            </Card>

            <Card className="bg-slate-950/60 border border-white/10 lexi-sector loi-row">
              <CardHeader>
                <CardTitle>Bates Anchor Map</CardTitle>
                <CardSubtitle>Enterprise reservation certificate</CardSubtitle>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {batesAssigned.length ? (
                    batesAssigned.map((item) => (
                      <div key={item} className="rounded-2xl border border-white/10 bg-black/50 p-3">
                        <div className="text-xs text-slate-200">{item}</div>
                        <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-300">
                          Reservation: ISSUED
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-slate-500">Awaiting Bates reservation.</div>
                  )}
                </div>
              </CardBody>
            </Card>
          </div>

          <Card className="bg-slate-950/60 border border-white/10 lexi-sector">
            <CardHeader>
              <CardTitle>Sovereignty Hub</CardTitle>
              <CardSubtitle>Corporate facts + zero-egress confirmation</CardSubtitle>
            </CardHeader>
            <CardBody>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-[10px] uppercase tracking-[0.35em] text-slate-500">
                  Diagnostic Toggle
                </div>
                <Button
                  variant={diagnosticMode ? "primary" : "secondary"}
                  size="sm"
                  onClick={toggleDiagnostic}
                  id="hold-toggle"
                >
                  {diagnosticMode ? "Diagnostic Mode: ON" : "Diagnostic Mode: OFF"}
                </Button>
              </div>
              <div className="mt-4 rounded-2xl border border-white/10 bg-black/50 p-4">
                <div className="text-[10px] uppercase tracking-[0.35em] text-slate-500">System Status</div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-300">
                  <div className="rounded-xl border border-white/10 bg-black/60 p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Workspace</div>
                    <div className="mt-2 font-mono text-slate-200">{workspaceId || "unassigned"}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-black/60 p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Ledger Status</div>
                    <div className="mt-2 font-mono text-emerald-200">
                      {liveIntegrity.systemHealth || "UNKNOWN"}
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-black/60 p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Diagnostics</div>
                    <div className={`mt-2 font-mono ${diagnosticMode ? "text-emerald-200" : "text-slate-400"}`}>
                      {diagnosticMode ? "ENABLED" : "OFF"}
                    </div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="xl:col-span-4 space-y-4">
          <Card id="integrity-meter" className="bg-slate-950/60 border border-white/10 lexi-sector">
            <CardHeader>
              <CardTitle>Integrity Pulse</CardTitle>
              <CardSubtitle>Signed ledger events</CardSubtitle>
            </CardHeader>
            <CardBody>
              <div className="rounded-2xl border border-white/10 bg-black/60 p-4 font-mono text-[10px] text-emerald-200 space-y-2 max-h-56 overflow-auto">
                {liveIntegrity.events.length ? (
                  liveIntegrity.events.map((event) => (
                    <div key={event.id} className="flex items-center justify-between gap-2">
                      <span>{event.action}</span>
                      <span className="mono text-emerald-300">{event.hash.slice(0, 10)}...</span>
                    </div>
                  ))
                ) : (
                  <div className="text-slate-500">Awaiting ledger activity.</div>
                )}
              </div>
            </CardBody>
          </Card>

          <Card className="bg-slate-950/60 border border-white/10 lexi-sector">
            <CardHeader>
              <CardTitle>Discovery Triage</CardTitle>
              <CardSubtitle>Legal weight vs forensic certainty</CardSubtitle>
            </CardHeader>
            <CardBody>
              <div className="relative h-40 rounded-2xl border border-white/10 bg-black/60">
                <div className="absolute inset-0 grid grid-cols-4 grid-rows-4 gap-[1px] opacity-30">
                  {Array.from({ length: 16 }).map((_, idx) => (
                    <div key={idx} className="border border-white/5" />
                  ))}
                </div>
                {scatterDots.map((dot) => (
                  <span
                    key={dot.id}
                    className={`absolute h-2 w-2 rounded-full ${dot.tone} shadow-[0_0_8px_rgba(16,185,129,0.6)]`}
                    style={{ left: `${dot.x}%`, top: `${dot.y}%` }}
                  />
                ))}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-slate-500">
                Axis: Legal Weight (x) / Forensic Certainty (y)
              </div>
            </CardBody>
          </Card>

          <Card className="bg-slate-950/60 border border-white/10 lexi-sector">
            <CardHeader>
              <CardTitle>Active Aigis Shield</CardTitle>
              <CardSubtitle>Outbound egress guard</CardSubtitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-xs">
                <span className="text-slate-400">Outbound Egress Attempts</span>
                <span className="font-mono text-emerald-300">0</span>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/60 p-3 text-[10px] font-mono text-slate-400 space-y-1">
                <div>&gt; [{new Date().toISOString()}] Air-gap enforced.</div>
                <div>&gt; Shield gate: no external calls permitted.</div>
              </div>
            </CardBody>
          </Card>

          <Card className="bg-slate-950/60 border border-white/10 lexi-sector">
            <CardHeader>
              <CardTitle>Aigis Dock</CardTitle>
              <CardSubtitle>Sentinel narration stream</CardSubtitle>
            </CardHeader>
            <CardBody>
              <div className="text-xs text-slate-500">Awaiting sentinel narration.</div>
              <div className="mt-3 max-h-48 overflow-auto">
                <ThoughtTrace steps={[]} />
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </Page>
  );
}
